<template>
  <div>
    <TopBar currentTitle="Ingredients">
      <b-button type="is-primary" outlined size="is-small" icon-left="upload" @click="$router.push({ name: 'BulkImportIngredientsView' })">Bulk Import</b-button>
      <b-button type="is-primary" size="is-small" icon-left="plus" @click="createNewIngredient">Add Ingredient</b-button>
    </TopBar>
    <div class="page-content">
    <h1 class="page-title">Ingredients</h1>
    <p class="page-subtitle mb-4">
      <template v-if="hasActiveFilter || searchQuery">{{ filteredIngredients.length }} of {{ ingredients.length }} ingredients</template>
      <template v-else>{{ ingredients.length }} total</template>
    </p>

    <!-- Filters Bar -->
    <div class="filters-bar">
      <div class="search-wrapper">
        <b-icon icon="magnify" class="search-icon" />
        <b-input
          placeholder="Search ingredients..."
          v-model="searchQuery"
          expanded>
        </b-input>
      </div>
      <div class="filters-right">
        <a v-if="hasActiveFilter || searchQuery" class="clear-filters" @click="clearAll">Clear all</a>
        <b-select v-model="filterItemType" size="is-small" @input="onItemTypeFilterChange" :class="{ 'filter-active': filterItemType }">
          <option :value="null">All types</option>
          <option value="ingredient">Ingredients</option>
          <option value="grocery">Groceries</option>
          <option value="household">Household</option>
        </b-select>
        <b-select v-model="filterAisleId" size="is-small" @input="onAisleFilterChange" :class="{ 'filter-active': filterAisleId }">
          <option :value="null">All aisles</option>
          <option v-for="aisle in availableAisles" :key="aisle.id" :value="aisle.id">{{ aisle.name }}</option>
        </b-select>
      </div>
    </div>

    <!-- Table Card -->
    <div class="feeds-card">
      <div class="feeds-card-body compact">
        <b-table
          :data="filteredIngredients"
          :hoverable="true"
          :paginated="true"
          :per-page="perPage"
          :current-page.sync="currentPage"
          :default-sort="[sortField, sortDirection]"
          @sort="onSort"
          pagination-position="bottom"
          @click="openIngredient">

          <b-table-column field="icon" label="" width="48" v-slot="props">
            <div v-if="props.row.icon_url" class="ingredient-icon" :style="{ backgroundColor: props.row.icon_color || '#7CB342' }">
              <img :src="props.row.icon_url" alt="" />
            </div>
            <div v-else class="ingredient-icon-placeholder">
              <b-icon icon="leaf" size="is-small" />
            </div>
          </b-table-column>

          <b-table-column field="name" label="Name" sortable v-slot="props">
            <p class="has-text-weight-bold">{{ props.row.name }}</p>
          </b-table-column>

          <b-table-column field="item_type" label="Type" sortable width="100" v-slot="props">
            <span class="type-tag" :class="props.row.item_type || 'ingredient'">{{ props.row.item_type || 'ingredient' }}</span>
          </b-table-column>

          <b-table-column field="aisle" label="Aisle" sortable v-slot="props">
            {{ props.row.aisle ? props.row.aisle.name : '-' }}
          </b-table-column>

          <b-table-column field="created_at" width=100 label="Created" sortable centered v-slot="props">
            <span class="date-tag">{{ new Date(props.row.created_at).toLocaleDateString() }}</span>
          </b-table-column>

          <b-table-column field="delete" custom-key="actions" width=40 centered v-slot="props">
            <b-button type="is-danger" size="is-small" icon-right="delete" @click.stop="deleteIngredient(props.row)" />
          </b-table-column>

          <template #empty>
            <div class="has-text-centered py-5">
              <p class="has-text-grey">No ingredients found</p>
            </div>
          </template>
        </b-table>
        <div class="pagination-footer">
          <div class="page-size-selector">
            <span class="page-size-label">Show:</span>
            <b-select v-model="perPage" size="is-small" @input="savePerPage">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </b-select>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import TopBar from '@/components/layout/TopBar.vue'

const STORAGE_KEY = 'feeds_admin_ingredient_list_per_page'
const PAGE_STATE_KEY = 'feeds_list_page_IngredientListView'
const SCROLL_STATE_KEY = 'feeds_list_state_IngredientListView'
const SORT_STATE_KEY = 'feeds_list_sort_IngredientListView'
const FILTER_STATE_KEY = 'feeds_list_filter_IngredientListView'
const SEARCH_STATE_KEY = 'feeds_list_search_IngredientListView'

export default {
  name: 'IngredientListView',
  components: {
    TopBar
  },
  data() {
    const savedSort = JSON.parse(sessionStorage.getItem(SORT_STATE_KEY) || 'null')
    return {
      ingredients: [],
      searchQuery: sessionStorage.getItem(SEARCH_STATE_KEY) || "",
      perPage: parseInt(localStorage.getItem(STORAGE_KEY)) || 25,
      currentPage: parseInt(sessionStorage.getItem(PAGE_STATE_KEY)) || 1,
      sortField: savedSort?.field || 'created_at',
      sortDirection: savedSort?.direction || 'desc',
      scrollRestored: false,
      filterItemType: sessionStorage.getItem(FILTER_STATE_KEY + '_type') || null,
      filterAisleId: sessionStorage.getItem(FILTER_STATE_KEY + '_aisle') || null
    }
  },
  watch: {
    searchQuery(value) {
      sessionStorage.setItem(SEARCH_STATE_KEY, value)
      this.currentPage = 1
    },
    currentPage(newPage) {
      sessionStorage.setItem(PAGE_STATE_KEY, newPage)
    },
    ingredients() {
      // Restore scroll position after data loads
      if (!this.scrollRestored) {
        this.scrollRestored = true
        this.$nextTick(() => {
          const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollY > 0) {
              setTimeout(() => {
                window.scrollTo(state.scrollX, state.scrollY)
              }, 50)
            }
          }
        })
      }
    }
  },
  firestore: {
    ingredients: db.collection('ingredients').orderBy("created_at", "desc")
  },
  computed: {
    filteredIngredients() {
      let results = this.ingredients
      if (this.filterItemType) {
        results = results.filter(i => i.item_type === this.filterItemType)
      }
      if (this.filterAisleId) {
        results = results.filter(i => i.aisle && i.aisle.id === this.filterAisleId)
      }
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        results = results.filter(i => i.name && i.name.toLowerCase().includes(query))
      }
      return results
    },
    hasActiveFilter() {
      return !!this.filterItemType || !!this.filterAisleId
    },
    availableAisles() {
      const map = new Map()
      this.ingredients.forEach(i => {
        if (i.aisle && i.aisle.id && !map.has(i.aisle.id)) {
          map.set(i.aisle.id, { id: i.aisle.id, name: i.aisle.name, position: i.aisle.position || 0 })
        }
      })
      return Array.from(map.values()).sort((a, b) => {
        if (a.position !== b.position) return a.position - b.position
        return (a.name || '').localeCompare(b.name || '')
      })
    }
  },
  methods: {
    savePerPage(value) {
      localStorage.setItem(STORAGE_KEY, value)
    },
    onSort(field, direction) {
      this.sortField = field
      this.sortDirection = direction
      sessionStorage.setItem(SORT_STATE_KEY, JSON.stringify({ field, direction }))
    },
    onItemTypeFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY + '_type', value || '')
      this.currentPage = 1
    },
    onAisleFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY + '_aisle', value || '')
      this.currentPage = 1
    },
    clearAll() {
      this.filterItemType = null
      this.filterAisleId = null
      this.searchQuery = ''
      sessionStorage.removeItem(FILTER_STATE_KEY + '_type')
      sessionStorage.removeItem(FILTER_STATE_KEY + '_aisle')
      sessionStorage.removeItem(SEARCH_STATE_KEY)
      this.currentPage = 1
    },
    createNewIngredient() {
      db.collection("ingredients").add({
        created_at: Date.now(),
        name: null,
        aisle: null,
        tips: [],
        item_type: "ingredient",
        is_food: true,
        default_unit: null
      })
      .then((docRef) => {
        this.$router.push({ name: 'IngredientView', params: { id: docRef.id } })
      })
      .catch((error) => {
        console.error("Error adding document: ", error);
      });
    },
    openIngredient(ingredient) {
      this.$router.push({ name: 'IngredientView', params: { id: ingredient.id } })
    },
    deleteIngredient(ingredient) {
      this.$buefy.dialog.confirm({
        title: 'Deleting ingredient',
        message: `Are you sure you want to delete ingredient: <b><i>"${ingredient.name}"</i></b>? <br /><br />This action cannot be undone.`,
        confirmText: 'Delete Ingredient',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          db.collection("ingredients").doc(ingredient.id)
          .delete()
          .then(() => {
            this.$buefy.toast.open({type: 'is-success', message: `Ingredient successfully deleted!`})
          }).catch((error) => {
            this.$buefy.toast.open({type: 'is-danger', message: `Error deleting ingredient: ${error}`})
          });
        }
      })
    }
  }
}
</script>

<style scoped>
.search-wrapper {
  flex: 1;
  max-width: 400px;
  position: relative;
}

.search-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: var(--text-muted);
}

.search-wrapper .input {
  padding-left: 40px !important;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-label {
  font-size: 13px;
  color: var(--text-secondary);
}

.date-tag {
  font-size: 12px;
  color: var(--text-secondary);
}

.ingredient-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.ingredient-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.type-tag {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
}

.type-tag.ingredient {
  color: var(--feeds-green);
  background: var(--feeds-green-light, #e6f5f3);
}

.type-tag.grocery {
  color: #b45309;
  background: #fef3c7;
}

.type-tag.household {
  color: #6366f1;
  background: #eef2ff;
}

.ingredient-icon-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary, #f1f3f9);
  color: var(--text-muted, #94a3b8);
}
</style>
